/**
 * React Query hooks for the QA Policy Validation feature.
 *
 * Provides data fetching and mutation capabilities for admin users
 * to validate policy correctness against known tenant configurations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { QaValidationSummary, QaPolicy, UpsertQaValidationRequest } from '@/types/qaValidation'

export const qaValidationKeys = {
  all: ['qa-validation'] as const,
}

/**
 * Fetch the full QA validation summary including all policies and aggregate counts.
 */
export function useQaValidation() {
  return useQuery<QaValidationSummary>({
    queryKey: qaValidationKeys.all,
    queryFn: () => api.getQaValidation(),
    staleTime: 30_000,
  })
}

/**
 * Upsert a QA validation result for a single policy.
 * Performs an optimistic cache update to avoid a full refetch — the single
 * updated policy is patched into the cached summary and counts are recalculated.
 */
export function useUpsertQaValidation() {
  const queryClient = useQueryClient()

  return useMutation<QaPolicy, Error, { policyEntityId: string; body: UpsertQaValidationRequest }>({
    mutationFn: ({ policyEntityId, body }) =>
      api.upsertQaValidation(policyEntityId, body),
    onSuccess: (updated) => {
      queryClient.setQueryData<QaValidationSummary>(qaValidationKeys.all, (old) => {
        if (!old) return old

        const policies = old.policies.map(p =>
          p.policyEntityId === updated.policyEntityId ? updated : p
        )
        const tested = policies.filter(p => p.status !== 'Untested').length

        return {
          ...old,
          tested,
          pass: policies.filter(p => p.status === 'Pass').length,
          fail: policies.filter(p => p.status === 'Fail').length,
          investigate: policies.filter(p => p.status === 'Investigate').length,
          policies,
        }
      })
    },
  })
}
